import { QRType } from '@/stores/qrStore';

export const qrTypeConfig: Record<QRType, { label: string; icon: string; description: string }> = {
  url: { label: 'URL', icon: 'Link', description: 'Link to any website' },
  vcard: { label: 'vCard', icon: 'Contact', description: 'Share contact details' },
  wifi: { label: 'WiFi', icon: 'Wifi', description: 'Connect to WiFi network' },
  email: { label: 'Email', icon: 'Mail', description: 'Pre-filled email' },
  sms: { label: 'SMS', icon: 'MessageSquare', description: 'Pre-filled text message' },
  whatsapp: { label: 'WhatsApp', icon: 'MessageCircle', description: 'WhatsApp message' },
  phone: { label: 'Phone', icon: 'Phone', description: 'Direct phone call' },
  location: { label: 'Location', icon: 'MapPin', description: 'GPS coordinates' },
  text: { label: 'Plain Text', icon: 'FileText', description: 'Any text content' },
  calendar: { label: 'Calendar', icon: 'Calendar', description: 'Add event to calendar' },
  paypal: { label: 'PayPal', icon: 'DollarSign', description: 'PayPal payment link' },
  bitcoin: { label: 'Crypto', icon: 'Bitcoin', description: 'Cryptocurrency address' },
  appstore: { label: 'App Store', icon: 'Download', description: 'App download links' },
  pdf: { label: 'PDF', icon: 'FileText', description: 'Link to PDF document' },
  instagram: { label: 'Instagram', icon: 'Instagram', description: 'Instagram profile' },
  twitter: { label: 'Twitter/X', icon: 'Twitter', description: 'Twitter/X profile' },
  linkedin: { label: 'LinkedIn', icon: 'Linkedin', description: 'LinkedIn profile' },
  ar: { label: 'AR Experience', icon: 'Box', description: 'Augmented Reality 3D model' },
  bio_page: { label: 'Bio Page', icon: 'Layout', description: 'Linktree-style landing page' },
  ai_art: { label: 'AI Artistic', icon: 'Palette', description: 'Artistic AI-generated QR' },
  gamification: { label: 'Gamification', icon: 'Trophy', description: 'Scavenger hunts & rewards' },
  blockchain: { label: 'Blockchain', icon: 'Cpu', description: 'NFT & Digital Authenticity' },
};

export function generateQRContent(type: QRType, data: Record<string, string>): string {
  switch (type) {
    case 'url':
      return data.url || 'https://example.com';
      
    case 'vcard':
      // Use pre-generated vCard content if available (from new form)
      if (data.vcardContent) {
        return data.vcardContent;
      }
      // Legacy fallback
      return `BEGIN:VCARD
VERSION:3.0
N:${data.lastName || ''};${data.firstName || ''}
FN:${data.firstName || ''} ${data.lastName || ''}
ORG:${data.organization || ''}
TITLE:${data.title || ''}
TEL:${data.phone || ''}
EMAIL:${data.email || ''}
ADR:;;${data.address || ''}
URL:${data.website || ''}
END:VCARD`;

    case 'wifi':
      return `WIFI:T:${data.encryption || 'WPA'};S:${data.ssid || ''};P:${data.password || ''};H:${data.hidden === 'true' ? 'true' : 'false'};;`;
      
    case 'email':
      return `mailto:${data.email || ''}?subject=${encodeURIComponent(data.subject || '')}&body=${encodeURIComponent(data.body || '')}`;
      
    case 'sms':
      return `sms:${data.phone || ''}${data.message ? `?body=${encodeURIComponent(data.message)}` : ''}`;
      
    case 'whatsapp':
      return `https://wa.me/${data.phone?.replace(/\D/g, '') || ''}${data.message ? `?text=${encodeURIComponent(data.message)}` : ''}`;
      
    case 'phone':
      return `tel:${data.phone || ''}`;
      
    case 'location':
      return `geo:${data.latitude || '0'},${data.longitude || '0'}?q=${data.latitude || '0'},${data.longitude || '0'}(${encodeURIComponent(data.label || 'Location')})`;
      
    case 'text':
      return data.text || 'Hello World';
      
    case 'calendar':
      const start = data.startDate?.replace(/[-:]/g, '') || '';
      const end = data.endDate?.replace(/[-:]/g, '') || '';
      return `BEGIN:VEVENT
SUMMARY:${data.title || 'Event'}
DTSTART:${start}
DTEND:${end}
LOCATION:${data.location || ''}
DESCRIPTION:${data.description || ''}
END:VEVENT`;

    case 'paypal':
      return `https://paypal.me/${data.username || ''}${data.amount ? `/${data.amount}` : ''}`;
      
    case 'bitcoin':
      return `bitcoin:${data.address || ''}${data.amount ? `?amount=${data.amount}` : ''}`;
      
    case 'appstore':
      return data.url || 'https://apps.apple.com';
      
    case 'pdf':
      return data.url || 'https://example.com/document.pdf';
      
    case 'instagram':
      return `https://instagram.com/${data.username || ''}`;
      
    case 'twitter':
      return `https://twitter.com/${data.username || ''}`;
      
    case 'linkedin':
      return `https://linkedin.com/in/${data.username || ''}`;
      
    case 'ar':
      return data.url || 'https://nexqr.online';
      
    case 'bio_page':
      return data.slug ? `https://nexqr.online/bio/${data.slug}` : 'https://nexqr.online';
      
    case 'ai_art':
      return data.url || 'https://nexqr.online';
      
    case 'gamification':
      return data.url || 'https://nexqr.online';
      
    case 'blockchain':
      return data.url || 'https://nexqr.online';
      
    default:
      return 'https://nexqr.online';
  }
}
